"use client";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Carousel } from '../structure/carousel';
import { getContentfulEntriesByType, getContentfulEntryByEntryID } from "./contentful.delivery";
import { usePixelatedConfig } from '../config/config.client';
import { addToShoppingCart } from "../shoppingcart/shoppingcart.functions";
import { AddToCartButton, /* GoToCartButton */ ViewItemDetails } from "../shoppingcart/shoppingcart.components";
// import { Loading, ToggleLoading } from "../foundation/loading";	
import { SmartImage } from "../elements/smartimage";
import "../../css/pixelated.grid.scss";
import "./contentful.items.css";
const debug = false;
const contentfulContentType = "item";
/* ========== CONTENTFUL ITEMS PAGE ========== */
/**
 * ContentfulItems — Fetch and render a list of Contentful items (carousel or list) using provided API configuration.
 *
 * @params - none
 * @returns {JSX.Element} Rendered list of Contentful items or loading state.
 */
ContentfulItems.propTypes = {
/** no props */
};
export function ContentfulItems() {
    const [items, setItems] = useState([]);
    const [assets, setAssets] = useState([]);
    const config = usePixelatedConfig();
    const apiProps = {
        proxyURL: config?.integrations?.contentful?.proxyURL,
        base_url: config?.integrations?.contentful?.base_url ?? '',
        space_id: config?.integrations?.contentful?.space_id ?? '',
        environment: config?.integrations?.contentful?.environment ?? '',
        delivery_access_token: config?.integrations?.contentful?.delivery_access_token ?? '',
    };
    /**
     * paintItems — Convert Contentful API items and assets into rendered list nodes.
     *
     * @param {array} [props.items] - Array of Contentful item entries.
 * @param {array} [props.assets] - Array of Contentful asset objects used for resolving images.
     */
    paintItems.propTypes = {
        /** Array of Contentful items */
        items: PropTypes.array.isRequired,
        /** Array of Contentful assets */
        assets: PropTypes.array.isRequired,
    };
    function paintItems(props) {
        if (debug)
            console.log("Painting Contentful Items");
        let newItems = [];
        for (let key in props.items) {
            const item = props.items[key];
            const itemImagesMatches = [];
            const itemImages = item.fields.images ? item.fields.images : [];
            for (let imgKey in itemImages) {
                const img = itemImages[imgKey];
                const itemAssets = props.assets.filter((asset) => (asset.sys.id === img.sys.id));
                itemImagesMatches.push(...itemAssets);
            }
            itemImagesMatches.sort((a, b) => {
                return a.sys.createdAt < b.sys.createdAt ? -1 : 1;
            });
            if (itemImagesMatches.length > 0) {
                // Contentful Asset URLs start with two slashes, convert to absolute URL
                const imageUrl = itemImagesMatches[0].fields.file.url;
                item.fields.imageUrl = imageUrl.startsWith("//")
                    ? "https:" + imageUrl
                    : imageUrl;
                item.fields.imageAlt = itemImagesMatches[0].fields.title;
            }
            const newItem = _jsx(ContentfulListItem, { item: item }, item.sys.id);
            newItems.push(newItem);
        }
        return newItems;
    }
    async function fetchItems() {
        try {
            const response = await getContentfulEntriesByType({
                apiProps: apiProps,
                contentType: contentfulContentType
            });
            if (debug)
                console.log("Contentful API Get Items Data", response);
            setItems(response.items);
            setAssets(response.includes.Asset);
        }
        catch (error) {
            console.error("Error fetching Contentful items:", error);
        }
    }
    useEffect(() => {
        if (debug)
            console.log("Running useEffect");
        // DISABLING FOR NOW TO AVOID LOADING TWICE 
        // ToggleLoading(true);
        fetchItems();
        // ToggleLoading(false);
    }, [
        apiProps.base_url,
        apiProps.space_id,
        apiProps.environment,
        apiProps.delivery_access_token,
    ]);
    if (items.length > 0) {
        return (_jsxs(_Fragment, { children: [_jsx("div", { className: "contentful-items-header", children: items.length === 1
                        ? _jsx(ContentfulItemHeader, { title: `${items.length} Featured Item` })
                        : _jsx(ContentfulItemHeader, { title: `${items.length} Featured Items` }) }), _jsx("div", { id: "contentful-items", className: "contentful-items", children: paintItems({ items: items, assets: assets }) })] }));
    }
    else {
        return (_jsx("div", { id: "contentful-items", className: "contentful-items" }));
    }
}
/* ========== CONTENTFUL LIST ITEM ========== */
/**
 * ContentfulListItem — Render a single Contentful item with image, title and add-to-cart actions.
 *
 * @param {any} [props.item] - Single Contentful item entry to render.
 */
ContentfulListItem.propTypes = {
    /** Contentful item entry object */
    item: PropTypes.any.isRequired,
};
export function ContentfulListItem(props) {
    const thisItem = props.item;
    const itemURL = "./store/" + thisItem.sys.id;
    const itemURLTarget = "_self";
    const shoppingCartItem = {
        itemID: thisItem.sys.id,
        itemURL: itemURL,
        itemTitle: thisItem.fields.title,
        itemDescription: thisItem.fields.description || '',
        itemImageURL: thisItem.fields.imageUrl,
        itemQuantity: 1,
        itemInventory: thisItem.fields.quantity,
        itemCost: thisItem.fields.price,
        itemCurrency: thisItem.fields.priceCurrency || 'USD',
        itemIsShippable: thisItem.fields.isShippable !== false,
        itemWeight: typeof thisItem.fields.weight === 'number' ? thisItem.fields.weight : Number(thisItem.fields.weight ?? 0),
        itemWeightUnit: thisItem.fields.weightUnit || 'lb',
        itemType: 'product',
    };
    const itemImage = thisItem.fields.imageUrl;
    const config = usePixelatedConfig();
    const imgComponent = _jsx(SmartImage, { src: itemImage, title: thisItem.fields.title, alt: thisItem.fields.title, cloudinaryEnv: config?.integrations?.cloudinary?.product_env ?? undefined, cloudinaryDomain: config?.integrations?.cloudinary?.baseUrl ?? undefined, cloudinaryTransforms: config?.integrations?.cloudinary?.transforms ?? undefined });
    return (_jsxs("div", { className: "contentful-item row-12col", children: [_jsx("div", { className: "contentful-item-photo grid-s1-e5", children: itemURL
                    ? _jsx("a", { href: itemURL, target: itemURLTarget, rel: "noopener noreferrer", children: imgComponent })
                    : (imgComponent) }), _jsxs("div", { className: "contentful-item-body grid-s5-e13", children: [_jsx("div", { className: "contentful-item-header", children: itemURL
                            ? _jsx(ContentfulItemHeader, { url: itemURL, target: itemURLTarget, title: thisItem.fields.title })
                            : _jsx(ContentfulItemHeader, { title: thisItem.fields.title }) }), _jsxs("div", { className: "contentful-item-details grid12", children: [_jsxs("div", { children: [_jsx("b", { children: "Item ID: " }), thisItem.sys.id] }), _jsxs("div", { children: [_jsx("b", { children: "UPC ID: " }), thisItem.fields.id] }), _jsxs("div", { children: [_jsx("b", { children: "Quantity: " }), thisItem.fields.quantity] }), _jsxs("div", { children: [_jsx("b", { children: "Brand / Model: " }), thisItem.fields.brand, " ", thisItem.fields.model] }), _jsxs("div", { children: [_jsx("b", { children: "Listing Date: " }), thisItem.fields.date] })] }), _jsx("div", { className: "contentful-item-price", children: itemURL
                            ? _jsxs("a", { href: itemURL, target: itemURLTarget, rel: "noreferrer", children: ["$", thisItem.fields.price, " USD"] })
                            : "$" + thisItem.fields.price + " USD" }), _jsx("br", {}), _jsxs("div", { className: "contentful-item-addtocart", children: [_jsx(ViewItemDetails, { href: "/store", itemID: thisItem.sys.id }), _jsx(AddToCartButton, { handler: addToShoppingCart, item: shoppingCartItem, itemID: thisItem.sys.id })] })] })] }));
}
/* ========== CONTENTFUL ITEM HEADER ========== */
/**
 * ContentfulItemHeader — Render a title for a Contentful item and optionally wrap it in a link.
 *
 * @param {string} [props.title] - Item title text.
 * @param {string} [props.url] - Optional URL to link the title to.
 * @param {string} [props.target] - Optional link target attribute (e.g., '_blank').
 */
ContentfulItemHeader.propTypes = {
    /** Item title text */
    title: PropTypes.string.isRequired,
    /** Optional link URL for the title */
    url: PropTypes.string,
    /** Link target attribute (e.g., '_blank') */
    target: PropTypes.string,
};
export function ContentfulItemHeader(props) {
    return (_jsx("span", { children: props.url
            ? _jsx("a", { href: props.url, target: props.target ?? '', rel: "noopener noreferrer", children: _jsx("h2", { className: "", children: props.title }) })
            : _jsx("h2", { className: "", children: props.title }) }));
}
/* ========== CONTENTFUL ITEM DETAIL PAGE ========== */
/**
 * ContentfulItemDetail — Fetch and render a single Contentful item by entry ID.
 *
 * @param {string} [props.entry_id] - Entry ID of the specific Contentful item to fetch.
 */
ContentfulItemDetail.propTypes = {
    /** Entry ID of the item to fetch */
    entry_id: PropTypes.string.isRequired,
};
export function ContentfulItemDetail(props) {
    const [item, setItem] = useState({});
    const [assets, setAssets] = useState({});
    const [cards, setCards] = useState([]);
    const config = usePixelatedConfig();
    const apiProps = {
        proxyURL: config?.integrations?.contentful?.proxyURL,
        base_url: config?.integrations?.contentful?.base_url ?? '',
        space_id: config?.integrations?.contentful?.space_id ?? '',
        environment: config?.integrations?.contentful?.environment ?? '',
        delivery_access_token: config?.integrations?.contentful?.delivery_access_token ?? '',
    };
    useEffect(() => {
        if (debug)
            console.log("Running useEffect");
        async function fetchStuff() {
            try {
                // Get Item Data From Entry ID
                const entryResponse = await getContentfulEntryByEntryID({
                    apiProps: apiProps,
                    entry_id: props.entry_id
                });
                if (debug)
                    console.log("Contentful API Get Item Data", entryResponse);
                const item = entryResponse;
                setItem(item);
                // Get Item Assets From Entries
                const assetsResponse = await getContentfulEntriesByType({
                    apiProps: apiProps,
                    contentType: contentfulContentType
                });
                if (debug)
                    console.log("Contentful API Get Contentful Assets Data", assetsResponse);
                const assetImages = assetsResponse.includes.Asset;
                setAssets(assetImages);
                // Get Item Image URLs From Item Images and Entry Assets
                const itemImages = item.fields.images ? item.fields.images : [];
                const imageCards = [];
                for (let imgKey in itemImages) {
                    const img = itemImages[imgKey];
                    const itemAsset = assetImages.filter((asset) => (asset.sys.id === img.sys.id));
                    /* Contentful Asset URLs start with two slashes */
                    const absURL = (itemAsset[0].fields.file.url.startsWith("//"))
                        ? "https:" + itemAsset[0].fields.file.url
                        : itemAsset[0].fields.file.url;
                    if (itemAsset.length > 0) {
                        imageCards.push({
                            index: Number(imgKey),
                            cardIndex: imageCards.length,
                            cardLength: itemImages.length,
                            image: absURL,
                            imageAlt: itemAsset[0].fields.title,
                            headerText: "",
                            bodyText: "",
                            link: "",
                            linkTarget: "_self"
                        });
                    }
                }
                setCards(imageCards);
            }
            catch (error) {
                console.error("Error fetching Contentful item images and assets:", error);
            }
        }
        fetchStuff();
    }, [
        props.entry_id,
        apiProps.base_url,
        apiProps.space_id,
        apiProps.environment,
        apiProps.delivery_access_token,
    ]);
    if (item && Object.keys(item) && Object.keys(item).length > 0) {
        const thisItem = { ...item };
        if (debug)
            console.log(thisItem);
        const itemURL = undefined;
        const itemURLTarget = "_self";
        const shoppingCartItem = {
            itemID: thisItem.sys.id,
            itemURL: itemURL,
            itemTitle: thisItem.fields.title,
            itemDescription: thisItem.fields.description || '',
            itemImageURL: thisItem.fields.imageUrl,
            itemQuantity: 1,
            itemInventory: thisItem.fields.quantity,
            itemCost: thisItem.fields.price,
            itemIsShippable: thisItem.fields.isShippable !== false,
            itemWeight: typeof thisItem.fields.weight === 'number' ? thisItem.fields.weight : Number(thisItem.fields.weight ?? 0),
            itemWeightUnit: thisItem.fields.weightUnit || 'lb',
            itemType: 'product',
        };
        shoppingCartItem.itemURL = itemURL;
        return (_jsx(_Fragment, { children: _jsxs("div", { className: "contentful-item row-12col", children: [_jsx("div", { className: "contentful-item-header grid-s1-e13", children: itemURL
                            ? _jsx(ContentfulItemHeader, { url: itemURL, title: thisItem.fields.title })
                            : _jsx(ContentfulItemHeader, { title: thisItem.fields.title }) }), _jsx("br", {}), _jsx("div", { className: "contentful-item-photo-carousel grid-s1-e7", children: _jsx(Carousel, { cards: cards, draggable: true, imgFit: "contain" }) }), _jsxs("div", { className: "grid-s7-e13", children: [_jsx("div", { className: "contentful-item-details grid12", children: _jsx("div", { dangerouslySetInnerHTML: { __html: thisItem.fields.description.replace(/(<br\s*\/?>\s*){2,}/gi, '') } }) }), _jsx("br", {}), _jsxs("div", { className: "contentful-item-details grid12", children: [_jsxs("div", { children: [_jsx("b", { children: "Item ID: " }), thisItem.sys.id] }), _jsxs("div", { children: [_jsx("b", { children: "UPC ID: " }), thisItem.fields.id] }), _jsxs("div", { children: [_jsx("b", { children: "Quantity: " }), thisItem.fields.quantity] }), _jsxs("div", { children: [_jsx("b", { children: "Brand / Model: " }), thisItem.fields.brand, " ", thisItem.fields.model] }), _jsxs("div", { children: [_jsx("b", { children: "Listing Date: " }), thisItem.fields.date] }), _jsx("br", {})] }), _jsx("div", { className: "contentful-item-price", children: itemURL
                                    ? _jsxs("a", { href: itemURL, target: itemURLTarget, rel: "noreferrer", children: ["$", thisItem.fields.price, " USD"] })
                                    : "$" + thisItem.fields.price + " USD" }), _jsx("br", {}), _jsx("div", { className: "contentful-item-addtocart", children: _jsx(AddToCartButton, { handler: addToShoppingCart, item: shoppingCartItem, itemID: thisItem.sys.id }) })] })] }) }));
    }
    else {
        return (_jsx(_Fragment, { children: _jsx("div", { id: "contentful-items", className: "contentful-items", children: _jsx("div", { className: "centered", children: "Loading..." }) }) }));
    }
}
